import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Moon, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownabbeyAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Newtownabbey | North Belfast Stress Relief</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Newtownabbey from our south Belfast clinic on the Ormeau Road. Parenting stress, financial anxiety, dual-income family pressure. CNHC registered, ultra-convenient access."
        />
        <meta name="keywords" content="anxiety treatment Newtownabbey, stress management Rathcoole Glengormley, hypnotherapy Newtownabbey anxiety, parenting stress Newtownabbey, therapist North Belfast" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownabbey-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Newtownabbey | North Belfast Stress Relief" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Newtownabbey. Specialized support for parenting stress, financial anxiety, work-life balance challenges." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Newtownabbey",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Newtownabbey from Belfast clinic. Specializing in suburban family stress, parenting anxiety, financial worries, work-life balance, and dual-income family pressure.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownabbey-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Newtownabbey",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Newtownabbey for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road in south Belfast, a short run down the M2 into the city and then south along the Ormeau Road (A24). For most Newtownabbey addresses it is a straightforward drive of under half an hour outside peak times, so the clinic is well-positioned for regular anxiety treatment. Metro bus services also run along the Ormeau Road corridor, and online sessions via Zoom are available for maximum convenience."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with parenting stress and overwhelm?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Parenting stress is one of the most common issues I treat among Newtownabbey clients, particularly dual-income families juggling work and childcare. The pressure to be the perfect parent while maintaining a career, managing household finances, and keeping up with children's activities creates chronic overwhelm. Solution-focused hypnotherapy helps by reducing your overall stress baseline so daily challenges feel more manageable, improving sleep quality often disrupted by worry about your children, teaching rapid relaxation techniques you can use during stressful parenting moments, building confidence in your parenting decisions, and helping you set healthy boundaries between work and family time. Most clients notice improvement within 3-4 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer evening appointments for working parents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, I offer flexible appointment times including evenings to accommodate working parents and dual-income families. Many Newtownabbey clients already commute to Belfast for work, making an evening appointment on their way home very convenient. The short journey from Newtownabbey means you can fit professional anxiety treatment around your busy family schedule without adding significant travel time."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with financial anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, financial anxiety - constant worry about money, bills, mortgage payments, and providing for your family - responds very well to hypnotherapy. While hypnotherapy can't change your financial situation directly, it significantly reduces the anxiety response to financial stress, allowing you to think more clearly about solutions. Solution-focused hypnotherapy helps by calming the panic response when thinking about money, improving sleep disrupted by financial worry, reducing the physical symptoms of stress (tension, headaches, digestive issues), and helping you make clearer financial decisions without anxiety-driven responses. Most clients see significant improvement within 4-6 sessions, and a discounted 4-session package is available."
                }
              },
              {
                "@type": "Question",
                "name": "Is anxiety treatment affordable for working families in Newtownabbey?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, I've designed my pricing to be accessible for working families. Your first consultation is a comprehensive 90-minute session including assessment and your first hypnotherapy session, and a discounted 4-session package is available. Most clients achieve significant anxiety relief within 4-6 sessions. When you consider the impact chronic anxiety has on your work performance, relationships, and quality of life, this represents excellent value."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with work-life balance anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Work-life balance anxiety - the constant feeling you're failing at both work and parenting, guilt about not spending enough time with your children, and overwhelm from trying to do everything perfectly - is particularly common among dual-income families in Newtownabbey. Hypnotherapy helps by reducing the perfectionism that drives anxiety, building acceptance that 'good enough' is actually good enough, improving your ability to be present rather than constantly worrying about the next task, and helping you establish realistic boundaries around work time and family time. The goal isn't perfect balance (impossible) but reducing the anxiety around imperfect balance."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety & Stress Relief for Newtownabbey
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Newtownabbey from our Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">M5/M2 into Belfast</span> from Newtownabbey • <span className="font-semibold">short drive</span> to the Ormeau Road
                </p>
              </div>
              <p className="text-gray-600 mt-2">Glider bus service • Evening appointments available • Online sessions</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Anxiety Symptoms Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Do You Recognize These Signs of Anxiety?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-primary-50 rounded-xl p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
                  <Brain className="w-6 h-6 text-accent-600 mr-2" />
                  Mental Symptoms
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Constant worry about money, bills, and providing for family</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Guilt about not being a good enough parent or partner</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Racing thoughts at night about work and family responsibilities</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Feeling overwhelmed by daily demands and never catching up</span>
                  </li>
                </ul>
              </div>
              <div className="bg-accent-50 rounded-xl p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
                  <Heart className="w-6 h-6 text-primary-600 mr-2" />
                  Physical Symptoms
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tight chest, rapid heartbeat when thinking about responsibilities</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tense muscles, jaw clenching, tension headaches from stress</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Exhaustion despite sleeping (or can't sleep due to worry)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Snapping at family members due to stress overload</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <h3 className="font-bold text-lg text-gray-900 mb-2 flex items-center">
                <Users className="w-6 h-6 text-blue-600 mr-2" />
                Suburban Family Stress
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Newtownabbey families face unique anxiety triggers: dual-income pressure juggling two careers and childcare, financial stress from mortgages, childcare costs, and living expenses, parenting perfectionism amplified by social media comparison, time poverty with never enough hours for work, kids, household tasks, partner time, the Belfast commute adding 1-2 hours to each workday, and constant guilt about not being present enough for your children or partner. These aren't personal failures - they're the reality of modern suburban family life that hypnotherapy can help you manage without being overwhelmed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Relieves Anxiety & Stress
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8 text-center">
              Anxiety happens when your brain's threat detection system becomes overactive, treating daily stresses (work deadlines, parenting challenges, financial worry) as genuine threats. Solution-focused hypnotherapy retrains this response.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Lower Your Stress Baseline</h3>
                <p className="text-gray-700 leading-relaxed">
                  Hypnosis works directly with your subconscious mind to reduce your overall anxiety levels, so daily challenges feel more manageable rather than overwhelming.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mb-4">
                  <Moon className="w-6 h-6 text-accent-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Restore Restorative Sleep</h3>
                <p className="text-gray-700 leading-relaxed">
                  Parents often sacrifice sleep for work or family time, then can't sleep due to worry. Hypnotherapy teaches your brain new relaxation responses, restoring quality sleep.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Reduce Parenting Guilt</h3>
                <p className="text-gray-700 leading-relaxed">
                  Perfectionism creates chronic guilt. Hypnotherapy helps you build self-compassion and accept that 'good enough' parenting is actually excellent parenting.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="font-bold text-xl text-gray-900 mb-4 text-center">What Happens in a Session?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">Part 1: Understanding (30 min)</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We discuss what's been happening with your anxiety - work stress, parenting challenges, financial worries, whatever is overwhelming you. I explain the neuroscience of anxiety in simple terms so you understand why you're feeling this way and how we'll address it at a subconscious level.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">Part 2: Hypnosis (30 min)</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You'll sit comfortably (many clients say it's the most relaxed they've felt in years) while I guide you into deep relaxation. In this receptive state, your subconscious mind absorbs positive suggestions for calm, confidence, and resilience. You remain in control throughout.
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4 text-center">
                Most clients see improvement within 3-4 sessions, with each session building on the last to create lasting change that fits around your busy family schedule.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Ultra-Convenient Access from Newtownabbey
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-primary-50 rounded-xl p-8">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                  <Car className="w-6 h-6 text-accent-600 mr-2" />
                  By Car
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>From Newtownabbey:</strong> Take the M2 south into Belfast, then follow the Ormeau Road (A24) into south Belfast</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>From Rathcoole/Glengormley:</strong> Join the M5 then M2 for the same route into the city</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>By public transport:</strong> Metro services run along the Ormeau Road corridor, and Botanic and City Hospital are the nearest rail stations</span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Journey time:</strong> A straightforward drive of under half an hour from most Newtownabbey areas outside peak traffic</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 italic mt-4">
                  As the closest major town to our Belfast clinic, Newtownabbey offers unbeatable convenience for regular anxiety treatment.
                </p>
              </div>
              <div className="bg-accent-50 rounded-xl p-8">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-6 h-6 text-primary-600 mr-2" />
                  Flexible Options
                </h3>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <h4 className="font-semibold mb-2">Evening Appointments</h4>
                    <p className="leading-relaxed">
                      Perfect for working parents - schedule on your way home from Belfast (if you already commute), or pop over from Newtownabbey after the school run. Flexible evening slots available.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Online Sessions</h4>
                    <p className="leading-relaxed">
                      Can't make even the 10-minute journey? Online hypnotherapy via Zoom is just as effective as in-person. Many parents start with one in-person visit, then switch to online for ultimate convenience around nap times and school schedules.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Glider Bus Option</h4>
                    <p className="leading-relaxed">
                      Glider rapid transit connects Newtownabbey to Belfast city centre, making sessions accessible even if you don't drive. Perfect for those who prefer public transport.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newtownabbey Families Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "Between two jobs, three kids, and a mortgage, I was drowning in anxiety. Couldn't sleep, snapping at my husband, felt like I was failing at everything. Sharon's approach was so practical and science-based. After 4 sessions, I feel like I can breathe again. The 12-minute drive from Rathcoole was nothing."
                </p>
                <p className="font-semibold text-gray-900">K.H., Rathcoole</p>
                <p className="text-sm text-gray-600">Parenting stress & overwhelm</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "Financial anxiety was consuming me - couldn't stop worrying about bills, mortgage, providing for the kids. It was affecting my work performance which made the anxiety worse. Hypnotherapy didn't change my finances, but it changed how I responded to the stress. I can think clearly now instead of panicking."
                </p>
                <p className="font-semibold text-gray-900">D.M., Glengormley</p>
                <p className="text-sm text-gray-600">Financial anxiety & work stress</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "The guilt about not being a perfect mum was crushing. Sharon helped me see that 'good enough' is actually excellent when you're juggling a full-time job and two young children. I sleep better, snap less at the kids, and actually enjoy parenting again. The evening appointments worked perfectly around my schedule."
                </p>
                <p className="font-semibold text-gray-900">A.W., Monkstown</p>
                <p className="text-sm text-gray-600">Parenting guilt & work-life balance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Newtownabbey Families Choose Sharon Clare Hypnotherapy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Ultra-Convenient Location</h3>
                  <p className="text-gray-700 leading-relaxed">
                    A short drive from Newtownabbey via the M5/M2 into Belfast, we're an easily reached professional hypnotherapy service. Fits easily around work and family schedules.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Family-Friendly Approach</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Extensive experience with parenting stress, work-life balance, and the unique pressures facing dual-income suburban families.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Affordable Pricing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    A discounted 4-session package is available. Accessible for working families without compromising quality. Most clients achieve significant relief within 4-6 sessions.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Flexible Scheduling</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Evening appointments available for working parents. Online sessions via Zoom for ultimate convenience around nap times, school pickups, and busy schedules.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default NewtownabbeyAnxietyStress;
